import { Injectable } from '@angular/core';
import { AnswerDTO } from '@app/model/answerDTO.model';
import { SlideType, QuestionType, extractHTMLContent, MandatoryStatusEnum, SortingWidgetOption } from 'library-explorer';
import { SlideDTO } from '@app/model/slideDTO.model';
import { TranslateService } from '@ngx-translate/core';

import { isEqual } from 'lodash';
import { QuestionDTO } from '@app/model/questionDTO.model';
import { SlideBuilderSection } from '@app/model/slide-builder-section';
import { QuestionFillBlanksStyle } from '@app/model/enums/question-fill-blanks-style.enum';


@Injectable({
  providedIn: 'root'
})
export class SlidesHelperService {
  constructor(
    private readonly translateService: TranslateService) { }

  public mapSlideTypeToActivityLabel(type: SlideType | QuestionType): string {
    switch (type) {
      case SlideType.Hotspots:
        return 'CHAPTERS.activity_open_hotpots';
      case SlideType.SlideTextFlipcards:
        return 'CHAPTERS.activity_flip_all_flipcards';
      case SlideType.SlideAccordion:
        return 'CHAPTERS.activity_open_all_accordions';
      case QuestionType.FILL_BLANKS:
        return 'CHAPTERS.activity_fill_all_blanks';
      case QuestionType.MATCHING_QUESTION:
        return 'CHAPTERS.activity_connect_all_pairs';
      case QuestionType.ORDER_QUESTION:
        return 'CHAPTERS.activity_place_all_elements_order';
      case QuestionType.QUESTION_SORTING:
        return 'CHAPTERS.activity_sort_all_items';
      case QuestionType.QUESTION_ASSIGNMENT:
      case QuestionType.QUESTION_FREE_TEXT:
      case QuestionType.QUESTION_MULTICHOICE_TEXT:
      case QuestionType.BOOLEAN_QUESTION:
      case QuestionType.QUESTION_PRONUNCIATION:
      case QuestionType.QUESTION_HOTSPOTS:
        return 'CHAPTERS.activity_answer_all_questions';
      case SlideType.SlideTextVideo:
      case SlideType.SlideInteractiveVideo:
        return 'CHAPTERS.activity_watch_all_videos';
      case SlideType.SlideTextAudio:
        return 'CHAPTERS.activity_listen_all_audios';
      case SlideType.SlideBuckets:
        return 'CHAPTERS.activity_buckets';
      case SlideType.SlideCarousel:
        return 'CHAPTERS.view_all_slides';
    } 
  }

  public updateSlideWithSystemFields(slide: SlideDTO): SlideDTO {
    if (this.isQuestionSlide(slide.type)) {
      slide = this.mapToQuestionSlide(slide);
    }

    if (this.isInteractionSlide(slide.type)) {
      slide.isInteractive = true;
    }

    if (slide.bundle === SlideType.SlideBuilder) {
      slide = this.mapSlideBuilderColumnsWidgets(slide);
    }

    return slide;
  }

  public getSlideUncompletionRatio(slide: SlideDTO, includeInteractions: boolean, includeQuestions: boolean, includeMedia: boolean): number {
    if (!slide) {
      return 0;
    }

    const slides = slide.bundle === SlideType.SlideBuilder ? this.getAllSlideBuilderSlides(slide) : [slide];
  
    const requiredSlides = slides.filter(item => {
      return (item.isInteractive && this.isMandatory(item, includeInteractions))
        || (includeQuestions && item.isQuestion)
        || (item.mediaVideo && this.isMandatory(item, includeMedia));
    });

    if (requiredSlides.length === 0) {
      return 0;
    }

    const [uncompletedInteractions, uncompletedQuestions, uncompletedMediaSlides] = [
      this.getUncompletedInteractionsRatio(slide, includeInteractions),
      includeQuestions ? this.getUncompletedQuestions(slide).length : 0,
      this.getUncompletedMediaSlides(slide, includeMedia).length
    ];

    return 100 * (uncompletedInteractions + uncompletedQuestions + uncompletedMediaSlides) / requiredSlides?.length;
  }


  public getCountOfQuestionsAndInteractiveWidgets(slide: SlideDTO): number {
    if (slide.bundle !== SlideType.SlideBuilder) {
      return slide.isQuestion || slide.isInteractive ? 1 : 0;
    }

    const slides = this.getAllSlideBuilderSlides(slide);

    return slides.filter(slide => slide.isQuestion || slide.isInteractive).length
  }

  public getUncompletedInteractionsRatio(slide: SlideDTO, requiredToCompleteInteraction: boolean): number {
    const slides = this.getAllSlideBuilderSlides(slide);

    return slides
      .filter(slide => slide.isInteractive && this.isMandatory(slide, requiredToCompleteInteraction))
      .map(slide => slide.interactionCompletionRatio ? 1 - slide.interactionCompletionRatio : 1)
      .reduce((total, ratio) => total + ratio, 0);
  }

  public getSlideBuilderSlideById(slide: SlideDTO, id: string): SlideDTO {
    return this.getAllSlideBuilderSlides(slide).find(item => item.id === id);
  }

  public getUncompletedQuestions(slide: SlideDTO): SlideDTO[] {
    if (!slide.isQuestion) {
      return [];
    }

    if (slide.bundle !== SlideType.SlideBuilder) {
      return slide.isQuestionCompleted ? [slide] : [];
    }

    const slides = this.getAllSlideBuilderSlides(slide);

    return slides.filter(this.isUncompletedQuestion);
  }

  public getUncompletedInteractions(slide: SlideDTO, requiredToCompleteInteraction: boolean): SlideDTO[] {
    const slides = this.getAllSlideBuilderSlides(slide);

    return slides.filter(slide => slide.isInteractive && !slide.isInteractionCompleted && this.isMandatory(slide, requiredToCompleteInteraction))
  }

  public isMandatory(slide: SlideDTO, fallback: boolean): boolean {
    const mandatory = slide?.mandatory != null ? Number(slide.mandatory) : MandatoryStatusEnum.AUTO;

    switch (mandatory) {
      case MandatoryStatusEnum.YES:
        return true;
      case MandatoryStatusEnum.NO:
        return false;
      default:
        return fallback;
    }
  }

  public getUncompletedMediaSlides(slide: SlideDTO, requiredToWatchVideo: boolean): SlideDTO[] {
    const isVideoSlideAndNotWatched = (item: SlideDTO) =>
      item.mediaVideo && !item.isVideoWatched && this.isMandatory(item, requiredToWatchVideo);

    if (slide.bundle !== SlideType.SlideBuilder) {
      return isVideoSlideAndNotWatched(slide) ? [slide] : [];
    }

    const slides = this.getAllSlideBuilderSlides(slide);

    return slides.filter(slide => isVideoSlideAndNotWatched(slide));
  }

  public mapSlideBuilderColumnsWidgets(slide: SlideDTO): SlideDTO {
    if (slide.bundle !== SlideType.SlideBuilder) {
      return;
    }

    const sections = slide.items as any[];
    slide.items = sections.map(section => {
      section.items = this.mapSlideBuilderColumnWidgets(section.items);
      section.left_columns_items = this.mapSlideBuilderColumnWidgets(section.left_columns_items);
      section.right_columns_items = this.mapSlideBuilderColumnWidgets(section.right_columns_items);

      return section;
    });

    slide.isQuestion = slide.items.some(section => {
      return [section.items, section.left_columns_items, section.right_columns_items].some(list => {
        list = Array.isArray(list) ? list : [];
    
        return list.some(widget => widget.isQuestion);
      });
    });

    return slide;
  }

  public hasSpecificWidgetType(slide: SlideDTO | QuestionDTO, bundle: string): boolean {
    if (slide.type === SlideType.SlideBuilder) {
      const items = slide.items as SlideBuilderSection[];

      return items.some(section => {
        return [section.items, section.left_columns_items, section.right_columns_items].some(list => {
          return list?.some(widget => widget.type === bundle);
        });
      });
    }

    return slide.type === bundle;
  }

  public getSlideBuilderQuestionSlide(currentSlide: SlideDTO): SlideDTO {
    let slide: SlideDTO;
  
    switch (currentSlide.type) {
      case SlideType.SlideBuilder:
        const sections = currentSlide.items as any[];
        sections.some(section => {
          return [section.items, section.left_columns_items, section.right_columns_items].some(list => {
            list = list || [];
            return list.some(widget => {
              if (widget.isQuestion) {
                slide = widget;
              }

              return widget.isQuestion;
            });
          });
        });
        break;
      default:
        slide = currentSlide;
        break;
    }

    return slide;
  }

  public isUncompletedQuestion(slide: SlideDTO): boolean {
    if (!slide) {
      return false;
    }

    return slide.isQuestion && !slide.isQuestionCompleted;
  }

  public allBranchingAnswersVisited(slide: SlideDTO): boolean {
    if (!slide) {
      return true;
    }

    if (slide.type === QuestionType.QUESTION_HOTSPOTS && slide.hotspots?.clickspots?.length) {
      return !!slide.hotspots.clickspots.every(item => item?.visited);
    } else if (slide.answers?.length) {
      return !!slide.answers.every(item => item.visited);
    }

    return true;
  }

  public checkQuestionAnswer(slide: SlideDTO, answer: AnswerDTO): { isCorrect: boolean, correctAnswer: string[] } {
    switch (slide.type) {
      case QuestionType.BOOLEAN_QUESTION: {
        const correctAnswer = [this.translateService.instant(`COMMON.${slide.answer.toString()}`)];
        return { isCorrect: !!answer.answer === slide.answer, correctAnswer };
      }

      case QuestionType.QUESTION_MULTICHOICE_TEXT: {
        const checkAllAnswers = slide.checkAllAnswers;
        const correctItems = slide.answers.filter(item => item.correctAnswer);

        const correctAnswer = correctItems?.map(correct => {
          if (!correct.text) {
            return this.getQuestionAnswerCode(slide.answers.indexOf(correct));
          }

          return `${extractHTMLContent(correct.text)}`;
        });

        if (checkAllAnswers) {
          const correctIndexes = slide.originalAnswers
            .map((item, index) => item.correctAnswer ? index : null)
            .filter(index => index !== null);

          const sortedAnswersHash = answer.answersHash.sort((a, b) => +a - +b)
          return { isCorrect: isEqual(correctIndexes, sortedAnswersHash), correctAnswer };
        }

        answer.answersHash = answer.answersHash || [];
        const currentAnswers = answer.answersHash
          .map(item => slide.originalAnswers[item])
          .map(item => item.correctAnswer);

        return { isCorrect: currentAnswers.length && !currentAnswers.some(item => item === false), correctAnswer };
      }

      case QuestionType.ORDER_QUESTION: {
        const correctAnswer = slide.originalAnswers.map(item => {
          return `${extractHTMLContent(item.question)}`;
        });

        return { isCorrect: !answer.items.some(item => +item.questionHash !== item.order), correctAnswer };
      }

      case QuestionType.MATCHING_QUESTION: {
        const correctAnswer = slide.originalAnswers.map(item => {
          const answer = `${extractHTMLContent(item.answer as string)}`;
          const question = `${extractHTMLContent(item.question)}`;

          return `${question} - ${answer}`;
        });

        return { isCorrect: !answer.matchingItems.some(item => item.questionHash !== item.answersHash), correctAnswer };
      }

      case QuestionType.FILL_BLANKS: {
        const correctAnswer = slide.renderHelp.correctAnswer;
        const answers = answer.answer as Array<string>;
        const prepareTextForComparison = (text: string): string => text ? text.toLocaleLowerCase()?.replace(/[`’‘]/g, "'") : '';

        const isCorrect = !answers.some((item: string, i) => {
          const currentAnswer = prepareTextForComparison(item);
          const correctAnswer: string = slide.renderHelp.correctAnswer[i] || '';
          const correctAnswerAlternatives = (slide.additionalCorrectOptions && slide.additionalCorrectOptions[i + 1]) || [];

          return ![correctAnswer, ...correctAnswerAlternatives]
            .map(item => prepareTextForComparison(item))
            .some(answer => answer === currentAnswer);
        });

        return { isCorrect, correctAnswer };
      }
      case QuestionType.QUESTION_PRONUNCIATION: {
        return { isCorrect: answer.isCorrect, correctAnswer: null };
      }
      case QuestionType.QUESTION_SLIDER: {
        const isCorrect = slide.correctAnswer.toString() === answer.answer.toString();
        return { isCorrect: isCorrect, correctAnswer: [slide.correctAnswer.toString()] };
      }

      case QuestionType.QUESTION_HOTSPOTS: {
        const correctAnswerHash = slide?.hotspots?.clickspots?.find(item => !!item.correctAnswer)?.hash;
        const answerHash = answer.answersHash?.[0];
        const correntAnswerIndex = slide?.hotspots?.clickspots?.findIndex(item => item.correctAnswer);

        return { isCorrect: answerHash === correctAnswerHash, correctAnswer: [this.getQuestionAnswerCode(correntAnswerIndex)] };
      }

      case QuestionType.QUESTION_SCALE: {
        const correctItems = slide.originalAnswers.filter(item => item.correctAnswer);
        const correctAnswer = correctItems?.map(correct => `${extractHTMLContent(correct.text)}`);

        answer.answersHash = answer.answersHash || [];

        const currentAnswers = answer.answersHash
          .map(item => slide.originalAnswers[item])
          .map(item => item.correctAnswer);

        return { isCorrect: currentAnswers.length && !currentAnswers.some(item => item === false), correctAnswer };
      }

      case QuestionType.QUESTION_SORTING: {
        const slideCards = slide.items as SortingWidgetOption[];
    
        const isCorrect = !slideCards?.some(card => {
          const cardAnswer = answer?.answers?.find(item => item.questionHash === card.questionHash);
          return cardAnswer.bucket !== card.bucket?.id;
        });

        const correctAnswer = Object.values(
          slideCards.reduce<Record<string, { bucketText: string; bucketAnswers: string[], bucketIndex: number }>>((acc, card, index) => {
            const bucket = card.bucket;
            if (Array.isArray(bucket) || !bucket) {
              return acc;
            }

            const bucketIndex = slide.buckets?.findIndex(item => item.id === bucket.id) ?? 0;

            acc[bucket.id] ??= { bucketText: bucket.text, bucketAnswers: [], bucketIndex };
            
            acc[bucket.id].bucketAnswers.push(card.text ?? this.getQuestionAnswerCode(index));

            return acc;
          }, {})
        )
        .sort((firstBucket, secondBucket) => firstBucket.bucketIndex - secondBucket.bucketIndex)
        .map(({ bucketText, bucketAnswers }) => `${bucketText}: ${bucketAnswers.join(', ')}`);

        return { isCorrect, correctAnswer };
      }
    }
  }

  public getQuestionAnswerCode(index = 0): string {
    if (index === -1) {
      return null;
    }
  
    const START_CHART = 'A';
    const startCharCode = START_CHART.charCodeAt(0);
    return String.fromCharCode(startCharCode + index);
  }

  private mapSlideBuilderColumnWidgets(data: SlideDTO[]): SlideDTO[] {
    if (!data) {
      return [];
    }

    return data.map(item => this.updateSlideWithSystemFields(item));
  }

  private mapToQuestionSlide(slide: SlideDTO): SlideDTO {
    if (!this.isQuestionSlide(slide.type)) {
      return;
    }

    const mappedSlide = {
      ...slide,
      title: extractHTMLContent(slide.question),
      isQuestion: true,
      isQuestionCompleted: false
    };

    switch (slide.type) {
      case QuestionType.QUESTION_SORTING: {
        const slideCards = slide.items as SortingWidgetOption[];

        const items = slideCards?.map(item => ({ ...item, questionHash: item.id }));
        const buckets = this.shuffleArray(slide.buckets, slide.randomizeBuckets);

        mappedSlide.items = items;
        mappedSlide.buckets = buckets;
        break;
      }
      case QuestionType.MATCHING_QUESTION: {
        const slideAnswers = slide.items as AnswerDTO[];
        const answers = this.shuffleArray(slideAnswers.map((item, i) => (
          {
            text: item.answer,
            hash: i.toString()
          }
        )));
        const questions = this.shuffleArray(slideAnswers.map((item, i) => (
          {
            text: item.question,
            hash: i.toString()
          }
        )), slide.randomize);

        mappedSlide.originalAnswers = slideAnswers;
        mappedSlide.items = {
          answers, questions
        };
        break;
      }
      case QuestionType.ORDER_QUESTION: {
        const slideAnswers = slide.items as AnswerDTO[];
        const questions = this.shuffleArray(slideAnswers.map(item => (
          {
            text: extractHTMLContent(item.question),
            hash: item.order.toString()
          }
        )));

        mappedSlide.originalAnswers = slideAnswers;
        mappedSlide.items = {
          answers: [], questions
        };
        break;
      }
      case QuestionType.QUESTION_MULTICHOICE_TEXT: {
        const answers = this.shuffleArray(slide.answers.map((item, i) => (
          {
            text: extractHTMLContent(item.text),
            hash: i,
            correctAnswer: item.correctAnswer,
            mediaImage: item.mediaImage
          }
        )), slide.randomizeAnswers);

        mappedSlide.originalAnswers = slide.answers;
        mappedSlide.answers = answers;
        break;
      }
      case QuestionType.QUESTION_SCALE: {
        const answers = slide.answers.map((item, i) => ({
          text: extractHTMLContent(item.text),
          hash: i.toString()
        }));

        mappedSlide.originalAnswers = slide.answers;
        mappedSlide.answers = answers;
        break;
      }
      case QuestionType.FILL_BLANKS: {
        const additionalCorrectOptions = slide.additionalCorrectOptions
          ? Object.keys(slide.additionalCorrectOptions).reduce((total, key) => {
            const additionalOpt = slide.additionalCorrectOptions[key] || [];
            return [...total, ...additionalOpt];
          }, [])
          : [];
        mappedSlide.availableValues = slide.fillStyle === QuestionFillBlanksStyle.DRAG_AND_DROP ?
          this.shuffleArray([...slide.renderHelp.correctAnswer, ...additionalCorrectOptions, ...slide.wrongAnswers,]) : [];
        mappedSlide.text = slide.renderHelp.question;
        break;
      }
      case QuestionType.QUESTION_HOTSPOTS: {
        if (slide.hotspots.clickspots) {
          mappedSlide.hotspots.clickspots = slide.hotspots.clickspots.map((spot, index) => ({ ...spot, hash: index.toString() }))
        }

        break;
      }
    }

    return mappedSlide;
  }

  private getAllSlideBuilderSlides(data: SlideDTO): SlideDTO[] {
    if (data.bundle !== SlideType.SlideBuilder) {
      return [data];
    }

    const slideSections = data.items as any[];
  
    const slides: SlideDTO[] = slideSections.reduce((total, section) => {
      const { items = [], left_columns_items = [], right_columns_items = [] } = section;
      total.push(...items, ...left_columns_items, ...right_columns_items);

      return total;
    }, []);

    return slides;
  }

  private isQuestionSlide(type: QuestionType | SlideType): boolean {
    return Object.values(QuestionType).includes(type as QuestionType);
  }

  private isInteractionSlide(type: QuestionType | SlideType): boolean {
    const interactionSlides = [
      SlideType.SlideTextFlipcards, 
      SlideType.SlideAccordion, 
      SlideType.Hotspots,
      SlideType.SlideBuckets,
      SlideType.SlideTextAudio,
      SlideType.SlideCarousel
    ];

    return interactionSlides.indexOf(type as SlideType) !== -1;
  }

  private shuffleArray(array, shouldRandomize = true): any[] {
    if (!shouldRandomize) {
      return array;
    }

    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }

    return array;
  }

}
